import React, { useState } from "react";
import { Link } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { ShieldAlert, X, ArrowRight } from "lucide-react";

/**
 * Floating call-to-action for students who haven't finished KYC yet.
 * Sits bottom-right, dismissible for the current session (comes back next visit).
 * Only renders for students with kyc_status != "verified".
 */
export default function KycFloatingBanner() {
  const { user } = useAuth();
  const [hidden, setHidden] = useState(
    () => typeof window !== "undefined" && sessionStorage.getItem("hn_kyc_banner_dismissed") === "1"
  );

  if (!user || user.role !== "student") return null;
  if (user.kyc_status === "verified") return null;
  if (hidden) return null;

  const dismiss = () => {
    sessionStorage.setItem("hn_kyc_banner_dismissed", "1");
    setHidden(true);
  };

  const label =
    user.kyc_status === "pending" ? "KYC under review" :
    user.kyc_status === "rejected" ? "KYC rejected — re-upload" :
    "Complete KYC now";

  const isBlocking = user.kyc_status !== "pending";

  return (
    <div
      data-testid="kyc-floating-banner"
      className="fixed z-40 bottom-4 right-4 max-w-[calc(100vw-2rem)] sm:max-w-sm"
    >
      <div className="rounded-2xl shadow-xl border-2 border-vermilion-300 bg-white overflow-hidden animate-in fade-in slide-in-from-bottom-4 duration-500">
        <div className="bg-gradient-to-r from-vermilion-600 to-marigold-500 px-4 py-2 flex items-center gap-2 text-white">
          <ShieldAlert size={16} />
          <span className="font-bold text-sm">{label}</span>
          <button
            onClick={dismiss}
            data-testid="kyc-banner-dismiss"
            className="ml-auto text-white/80 hover:text-white p-1 rounded"
            aria-label="Dismiss KYC reminder"
          >
            <X size={14} />
          </button>
        </div>
        <div className="p-4 space-y-3">
          <p className="text-sm text-stone-800 leading-relaxed">
            {isBlocking
              ? "Upload your Aadhaar (front + back) to book any hostel. Approval usually takes under 24 hours."
              : "We're reviewing your Aadhaar. You'll get a WhatsApp + email once it's verified."}
          </p>
          {isBlocking && (
            <Link
              to="/kyc"
              data-testid="kyc-banner-cta"
              onClick={() => sessionStorage.removeItem("hn_kyc_banner_dismissed")}
              className="inline-flex items-center gap-1.5 bg-vermilion-600 hover:bg-vermilion-700 text-white text-sm font-bold rounded-full px-4 py-2 transition-colors"
            >
              Upload now <ArrowRight size={14} />
            </Link>
          )}
        </div>
      </div>
    </div>
  );
}
